import { memo, useCallback, useEffect, useState } from "react"
import { IconChevronDownOutline18 as ChevronDown } from "nucleo-ui-outline-18"
import { z } from "zod"

import type { Project } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { IconButton, Input, Switch, Tooltip } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuLabel,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"

interface EarlyAccessToggleProps {
	project: Project
}

export const EarlyAccessToggle = memo(({ project }: EarlyAccessToggleProps) => {
	const zero = useZero()
	const { role } = useRole()
	const [url, setUrl] = useState(project.betaUrl ?? "")
	const [isOpen, setIsOpen] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const isEarlyAccess = project.archived ?? false

	useEffect(() => {
		setUrl(project.betaUrl ?? "")
	}, [project.betaUrl])

	const handleSave = useCallback(async () => {
		if (!url) {
			setError(null)
			if (isEarlyAccess) {
				await mutateOk(
					zero.mutate(
						mutators.projects.update({
							id: project.id,
							betaUrl: null,
						}),
					),
				)
			}
			return true
		}

		const result = z
			.string()
			.url()
			.refine(
				(val) => val.startsWith("http://") || val.startsWith("https://"),
				{
					message: "Must start with http:// or https://",
				},
			)
			.safeParse(url)
		if (result.success) {
			setError(null)
			if (isEarlyAccess) {
				await mutateOk(
					zero.mutate(
						mutators.projects.update({
							id: project.id,
							betaUrl: url,
						}),
					),
				)
			}
			return true
		} else {
			// Show specific error message if available, otherwise generic
			setError(result.error.issues[0]?.message || "Please enter a valid URL")
			return false
		}
	}, [url, isEarlyAccess, project.id, zero])

	const handleOpenChange = useCallback(
		async (open: boolean) => {
			if (!open) {
				// Save the URL when closing the menu
				const isValid = await handleSave()
				if (!isValid) {
					// If validation failed, reset to original value
					setUrl(project.betaUrl ?? "")
				}
				setError(null)
			}
			setIsOpen(open)
		},
		[project.betaUrl, handleSave],
	)

	const handleToggle = useCallback(
		async (checked: boolean) => {
			await mutateOk(
				zero.mutate(
					mutators.projects.update({
						id: project.id,
						archived: checked,
						betaUrl: checked ? url || null : null,
					}),
				),
			)

			// Open dropdown when enabling early access
			if (checked) {
				setIsOpen(true)
			}
		},
		[project.id, zero, url],
	)

	const handleUrlChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setUrl(e.target.value)
			if (error) setError(null)
		},
		[error],
	)

	const handleUrlBlur = useCallback(async () => {
		await handleSave()
	}, [handleSave])

	const handleKeyDown = useCallback(
		async (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				const isValid = await handleSave()
				if (isValid) {
					setIsOpen(false)
				}
			}
		},
		[handleSave],
	)

	if (role === "VIEWER") {
		return null
	}

	return (
		<div className="group flex flex-row items-center gap-0">
			<Tooltip label="This will change the upvote button on your portal to an 'Early access' button.">
				<div
					className={cn(
						"flex flex-row items-center gap-2 rounded-md px-2 py-0.5 hover:bg-gray-100 dark:hover:bg-gray-800",
					)}
				>
					<Switch
						name={`early-access-toggle-${project.id}`}
						checked={isEarlyAccess}
						onCheckedChange={handleToggle}
						label="Mark as early access"
						direction="switch-first"
						size="small"
						labelClassName="mt-0.5 select-none text-sm font-medium text-primary"
					/>
				</div>
			</Tooltip>

			{isEarlyAccess && (
				<DropdownMenu
					trigger={
						<IconButton
							Icon={ChevronDown}
							variant="ghost"
							className="opacity-0 group-hover:opacity-100"
							size="sm"
						/>
					}
					open={isOpen}
					onOpenChange={handleOpenChange}
					contentProps={{ align: "start", side: "bottom" }}
					className="w-80"
				>
					<DropdownMenuGroup>
						<DropdownMenuLabel>Early access URL</DropdownMenuLabel>
						<div
							role="presentation"
							className="px-2 pb-2"
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}
						>
							<Input
								name={`early-access-url-${project.id}`}
								type="url"
								placeholder="https://..."
								value={url}
								onChange={handleUrlChange}
								onBlur={handleUrlBlur}
								onKeyDown={handleKeyDown}
								className={cn(
									"h-8 text-sm",
									error && "border-red-500 focus:border-red-500",
								)}
							/>
							{error ? (
								<p className="mt-1.5 text-xs text-red-500">{error}</p>
							) : (
								<p className="mt-1.5 text-xs text-tertiary">
									If set, clicking the early access button will open this URL.
								</p>
							)}
						</div>
					</DropdownMenuGroup>
				</DropdownMenu>
			)}
		</div>
	)
})
